// ── Web Audio API Sound FX Engine ────────────────────────────────────────────
// All sounds are synthesized procedurally — no external files needed.

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let _muted = false;

function getCtx(): AudioContext {
  if (!ctx) {
    ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.18;
    masterGain.connect(ctx.destination);
  }
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
}

export function setMuted(muted: boolean) {
  _muted = muted;
  if (masterGain) masterGain.gain.value = muted ? 0 : 0.18;
}

export function isMuted() { return _muted; }

function play(fn: (ctx: AudioContext, out: AudioNode) => void) {
  if (_muted) return;
  try {
    const c = getCtx();
    fn(c, masterGain!);
  } catch (_) {}
}

// Cyberpunk click — short digital tick
export function sfxClick() {
  play((c, out) => {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.connect(g); g.connect(out);
    osc.type = 'square';
    osc.frequency.setValueAtTime(1200, c.currentTime);
    osc.frequency.exponentialRampToValueAtTime(400, c.currentTime + 0.06);
    g.gain.setValueAtTime(0.3, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.06);
    osc.start(c.currentTime);
    osc.stop(c.currentTime + 0.06);
  });
}

// Neon hover — soft high-pitched shimmer
export function sfxHover() {
  play((c, out) => {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.connect(g); g.connect(out);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(2400, c.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1800, c.currentTime + 0.12);
    g.gain.setValueAtTime(0.08, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.12);
    osc.start(c.currentTime);
    osc.stop(c.currentTime + 0.12);
  });
}

// Whoosh — navigation transition
export function sfxWhoosh() {
  play((c, out) => {
    const buf = c.createBuffer(1, c.sampleRate * 0.25, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = c.createBufferSource();
    src.buffer = buf;
    const filter = c.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(800, c.currentTime);
    filter.frequency.exponentialRampToValueAtTime(3200, c.currentTime + 0.25);
    filter.Q.value = 2;
    const g = c.createGain();
    g.gain.setValueAtTime(0.25, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.25);
    src.connect(filter); filter.connect(g); g.connect(out);
    src.start(c.currentTime);
  });
}

// Power-up — opening cart / modal
export function sfxPowerUp() {
  play((c, out) => {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.connect(g); g.connect(out);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(200, c.currentTime);
    osc.frequency.exponentialRampToValueAtTime(800, c.currentTime + 0.3);
    g.gain.setValueAtTime(0.15, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.3);
    osc.start(c.currentTime);
    osc.stop(c.currentTime + 0.3);
  });
}

// Neon hum — ambient background (looping)
let humOsc: OscillatorNode | null = null;
export function startHum() {
  if (_muted || humOsc) return;
  try {
    const c = getCtx();
    humOsc = c.createOscillator();
    const g = c.createGain();
    const filter = c.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 120;
    humOsc.type = 'sawtooth';
    humOsc.frequency.value = 60;
    g.gain.value = 0.04;
    humOsc.connect(filter); filter.connect(g); g.connect(masterGain!);
    humOsc.start();
  } catch (_) {}
}

export function stopHum() {
  if (humOsc) { try { humOsc.stop(); } catch (_) {} humOsc = null; }
}
